// The price, live, from whatever the checklist knows so far.
//
// Unlike the placeholder it replaces, this does not wait for a complete
// checklist. The model was trained with explicit "not stated" indicators on the
// answers most often missing, so a scope that is half filled in is something it
// was built to score rather than something it has to guess at. The number
// exists from the moment a company is chosen and sharpens as the call goes on,
// which is the whole point of pricing during the call instead of after it.
//
// What it must not do is look more certain than it is. Hence the bands, which
// are empirical residual quantiles rather than multiples of a standard
// deviation - the residuals are fat-tailed, and a normal-theory band would
// overstate the tight end and understate the wide one.

import { priceFor } from '../pricing/scope';
import { SPEC } from '../pricing/score';

const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

/** Model feature names are column names. These are the words for them. */
const DRIVER_LABELS: Record<string, string> = {
  total_sqft: 'Affected square footage',
  non_room_sections: 'Job-wide sections',
  flag_containment: 'Containment',
  flag_asbestos: 'Asbestos',
  'total_sqft not stated': 'Square footage not stated',
  'flag_containment not stated': 'Containment not established',
  'flag_asbestos not stated': 'Asbestos not established',
};

function driverLabel(name: string): string {
  if (DRIVER_LABELS[name]) return DRIVER_LABELS[name]!;
  // "org_id=JDR" and "project_type=Water Mitigation" read fine as their value.
  const [, value] = name.split('=');
  if (value) return value;
  if (name.startsWith('room_')) {
    const word = name.slice(5).replace(/_/g, ' ');
    return word.charAt(0).toUpperCase() + word.slice(1);
  }
  return name;
}

export function PriceEstimate({ answers }: { answers: Record<string, string | null> }) {
  const estimate = priceFor(answers);

  if (!estimate) {
    return (
      <div className="estimate estimate--waiting">
        <span className="estimate__label">Estimated project price</span>
        <span className="estimate__note">
          Choose a company above and the estimate appears, then sharpens as the call fills in.
        </span>
      </div>
    );
  }

  const median = SPEC.training_price_quantiles['50'];

  return (
    <div className="estimate">
      <span className="estimate__label">Estimated project price</span>
      <span className="estimate__value">{money.format(estimate.mid)}</span>

      <div className="estimate__bands">
        <span>
          <b>{money.format(estimate.lo50)}</b> to <b>{money.format(estimate.hi50)}</b>
          <span className="estimate__tag">half of jobs</span>
        </span>
        <span className="estimate__wide">
          {money.format(estimate.lo80)} to {money.format(estimate.hi80)} &mdash; 8 in 10
        </span>
      </div>

      {estimate.drivers.length > 0 && (
        <ul className="drivers">
          {estimate.drivers.map((d) => {
            const pct = Math.round((d.multiplier - 1) * 100);
            return (
              <li key={d.name}>
                <span className="drivers__name">{driverLabel(d.name)}</span>
                <span className={`drivers__pct${pct >= 0 ? '' : ' drivers__pct--down'}`}>
                  {pct >= 0 ? '+' : ''}
                  {pct}%
                </span>
              </li>
            );
          })}
        </ul>
      )}

      <span className="estimate__note">
        What comparable jobs came in at &mdash; not a quote. Mean outcomes run about{' '}
        {Math.round((SPEC.smearing - 1) * 100)}% higher than this figure; the price distribution has
        a long right tail.{median ? ` Median priced job: ${money.format(median)}.` : ''}
      </span>
    </div>
  );
}
